import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert, Button, Tabs, Typography } from 'antd';

import isBefore from 'date-fns/isBefore';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import ProductsTour from 'v2/components/Shared/Tour';
import { useListIrregularProducts } from 'v2/hooks/products/useListIrregularProducts';
import { useMyProducts } from 'v2/hooks/products/useMyProducts';

import { Icon } from '@eduzz/ui-antd-theme';

import CoProductions from './components/CoProductions';
import { InputProductSearch } from './components/InputProductSearch';
import Irregular from './components/Irregular';
import ProductTable from './components/ProductTable';
import QuickView from './components/QuickView';
import type { TableType } from './constants';
import { TabsIndexes, tabs } from './constants';
import { productTourSteps } from './tour';

const isBeforeAttentionDate = isBefore(new Date(), new Date('2025-02-04'));

function getInitialFilters(tabParam?: string) {
  if (tabParam === 'approved') return { moderation_status: 'approved', status: '', order: 'created_at-desc' };
  if (tabParam === 'reproved') return { moderation_status: 'reproved', status: '', order: 'created_at-desc' };
  if (tabParam === 'inactive') return { moderation_status: '', status: 'inactive', order: 'created_at-desc' };
  return { moderation_status: '', status: '', order: 'created_at-desc' };
}

function getInitialTab(tabParam?: string) {
  if (tabParam === 'approved') return TabsIndexes.APPROVED;
  if (tabParam === 'reproved') return TabsIndexes.REPROVED;
  if (tabParam === 'inactive') return TabsIndexes.INACTIVE;
  return TabsIndexes.ALL;
}

const MyContents = () => {
  const params = useParams<{ tab?: string }>();

  const [tab, setTab] = useState(() => getInitialTab(params?.tab));
  const [productFilters, setProductFilters] = useState(() => getInitialFilters(params?.tab));
  const [productPagination, setProductPagination] = useState({ current_page: 1, per_page: 10 });
  const [activeQuickView, setActiveQuickView] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [dataProduct, setDataProduct] = useState<any>(null);
  const [dataActivatedChildren, setDataActivatedChildren] = useState<any>(null);

  const pageLoadRef = useRef({ startTime: performance.now(), completed: false });
  const prevIrregularProductsLengthRef = useRef<number>(0);
  const searchValueRef = useRef(searchValue);
  searchValueRef.current = searchValue;

  const {
    data: products,
    pagination,
    isFetching,
    isCompleted,
    refetch
  } = useMyProducts(productFilters, productPagination);
  const { data: irregularProducts } = useListIrregularProducts({ current_page: 1, per_page: 10 }, '');

  const handleSearchEnter = useCallback(
    (value: string) => {
      switch (tab) {
        case TabsIndexes.COPRODUCTIONS:
        case TabsIndexes.IRREGULAR:
          setCommittedSearch(value);
          break;

        default:
          setProductFilters(prev => ({ ...prev, title: value }));
          setProductPagination(prev => ({ ...prev, current_page: 1 }));
          break;
      }
    },
    [tab]
  );

  const handleTabChange = useCallback((value: string) => {
    const numValue = Number(value);
    setTab(numValue);

    if (numValue === TabsIndexes.COPRODUCTIONS || numValue === TabsIndexes.IRREGULAR) {
      setSearchValue('');
      setCommittedSearch('');
      return;
    }

    if (numValue === TabsIndexes.INACTIVE) {
      setProductFilters(prev => ({
        status: 'inactive',
        moderation_status: '',
        order: prev.order
      }));
    } else {
      const moderationStatusMap = ['all', 'approved', 'pendding', 'reproved'];
      setProductFilters(prev => ({
        status: '',
        moderation_status: moderationStatusMap[numValue] ?? '',
        order: prev.order
      }));
    }

    setProductPagination(prev => ({ ...prev, current_page: 1 }));
    setSearchValue('');
    setCommittedSearch('');
  }, []);

  const onChangeSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const onChangeRowsPerPage = useCallback((perPage: number) => {
    setProductPagination(prev => ({ ...prev, current_page: 1, per_page: perPage }));
  }, []);

  const onChangePage = useCallback((page: number) => {
    setProductPagination(prev => ({ ...prev, current_page: page }));
  }, []);

  const onClickOptions = useCallback((product: any, activatedChildren: any) => {
    setActiveQuickView(true);
    setDataProduct(product);
    setDataActivatedChildren(activatedChildren);
  }, []);

  const onEditProduct = useCallback(() => {
    if (searchValueRef.current) {
      handleSearchEnter(searchValueRef.current);
    } else {
      refetch();
    }
  }, [handleSearchEnter, refetch]);

  const onSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setProductFilters(prev => ({
      ...prev,
      order: `${field}-${direction}`
    }));
  }, []);

  const handlePressEnter = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        handleSearchEnter(searchValueRef.current);
      }
    },
    [handleSearchEnter]
  );

  useEffect(() => {
    const currentLength = irregularProducts?.length ?? 0;
    const prevLength = prevIrregularProductsLengthRef.current;

    if (currentLength !== prevLength && isBeforeAttentionDate) {
      setTab(TabsIndexes.IRREGULAR);
    }

    prevIrregularProductsLengthRef.current = currentLength;
  }, [irregularProducts]);

  useEffect(() => {
    if (!pageLoadRef.current.completed && isCompleted) {
      pageLoadRef.current.completed = true;
    }
  }, [isCompleted]);

  const coproductionsTabIndex = tabs.length;
  const irregularTabIndex = tabs.length + 1;

  const tabItems = [
    ...tabs.map((t, i) => ({ key: String(i), label: t })),
    { key: String(coproductionsTabIndex), label: 'Co-Produções' },
    ...(irregularProducts?.length && isBeforeAttentionDate
      ? [
          {
            key: String(irregularTabIndex),
            label: (
              <Typography.Text type='warning'>
                <Icon.TriangleAlert className='mr-1' />
                Atenção
              </Typography.Text>
            )
          }
        ]
      : [])
  ];

  return (
    <div id='my-products'>
      <TopPage
        title='Meus Produtos'
        extra='Crie, edite e acompanhe os seus produtos.'
        action={
          <PermissionHide action='myeduzz:meus-produtos:criar'>
            <Link to={'/producer/product/newproduct'}>
              <Button id='my-products-create' type='primary' icon={<Icon.Plus />}>
                Criar Novo Produto
              </Button>
            </Link>
          </PermissionHide>
        }
      />

      {!!irregularProducts?.length && isBeforeAttentionDate && (
        <div className='pb-4'>
          <Alert
            type='warning'
            message={
              <>
                <b>Atenção: Ajuste necessário nos seus produtos no MyEduzz</b>
                <br />
                Identificamos que alguns de seus produtos estão sem entregas cadastradas devido à exclusão do curso no
                Eduzz Membros. Essa situação pode gerar <b>reembolsos</b> e <b>chargebacks</b> em caso de vendas.
                <br />
                Para evitar problemas, pedimos que você ajuste as entregas dos produtos destacados na aba{' '}
                <b>{'"Atenção"'}</b>.
                <br />
                <b>Prazo:</b> Ajuste até <b>03/02/2025</b>.{' '}
                <b>
                  Após essa data, os produtos sem entregas cadastradas serão reprovados na moderação e suas vendas serão
                  suspensas
                </b>
                .
                <br />
                Em caso de dúvidas, entre em contato com o{' '}
                <Typography.Link href='https://orbita.eduzz.com/producer/help' target='_blank'>
                  atendimento através do chat
                </Typography.Link>
                .
              </>
            }
          />
        </div>
      )}

      <div className='flex min-w-0 flex-wrap items-center justify-between'>
        <Tabs
          id='my-products-tabs'
          className='min-w-0'
          activeKey={String(tab)}
          onChange={handleTabChange}
          items={tabItems}
        />
        {(tab < tabs.length || tab === coproductionsTabIndex) && (
          <InputProductSearch
            onChange={onChangeSearch}
            onKeyPress={handlePressEnter}
            value={searchValue}
            handleSearchEnter={handleSearchEnter}
          />
        )}
      </div>

      <div className='mt-4'>
        {tab < tabs.length && (
          <TabProductContent
            table={{
              products,
              order: productFilters.order,
              onSort,
              onClickOptions,
              isLoading: isFetching,
              pagination: {
                current_page: pagination?.current_page ?? 1,
                per_page: pagination?.per_page ?? 10,
                total: pagination?.total_items,
                optionsPerPage: '10,20,30,40',
                onChangePage,
                onChangePerPage: onChangeRowsPerPage
              }
            }}
          />
        )}

        {tab === coproductionsTabIndex && <CoProductions search={committedSearch} />}

        {tab === irregularTabIndex && !!irregularProducts?.length && isBeforeAttentionDate && (
          <Irregular search={committedSearch} />
        )}
      </div>
      <QuickView
        active={activeQuickView}
        changeActive={() => setActiveQuickView(prev => !prev)}
        onEditProduct={onEditProduct}
        productData={dataProduct}
        activatedChildren={dataActivatedChildren}
      />
    </div>
  );
};

const TabProductContent = memo(({ table }: { table: TableType }) => {
  return (
    <div id='myProductsList'>
      <ProductsTour name='products_tour' steps={productTourSteps} />
      <ProductTable {...table} />
    </div>
  );
});

export default MyContents;
